<template>
    <module-chart-with-options
        v-if="chartConfig"
        :chart-config="chartConfig"
        :title="title"
        :blocked-actions="blockedActions"
        @update:chart-date="updateChartDate"
        @update:chart-view="updateChartView"
        clear-series-on-remove
        :hide-date="props.hideDate"
        :is-modal="props.isModal"
        :display-options-top="props.displayOptionsTop"
    />
</template>

<script setup lang="ts">
import { IModuleChartConfig, ViewMode } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { chartApi } from '@/api/powerMarkets/chartApi.ts';
import { computed, ref, watch } from 'vue';
import { dateFormatToAnyFormat } from '@/utils/dateUtils.ts';
import { IChartModuleSeries } from '@/interfaces/powerMarkets/IModule.ts';
import { ITableDataConfig } from '@/interfaces/powerMarkets/table/ITableDataConfig.ts';

import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { cloneDeep } from '@/utils/global.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { IModuleBlockedActions, NON_USER_DASHBOARD_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import ModuleChartWithOptions from '../powerMarkets/chart/ModuleChartWithOptions.vue';

const props = defineProps<{
    seriesId: string;
    optionId?: string | null;
    countryId?: string;
    fromDate: string;
    toDate: string;
    multipleSeries?: boolean;
    multipleSeriesArray?: IChartModuleSeries[];
    title?: string;
    chartConfigId: string;
    hideDate?: boolean;
    blockedActions?: IModuleBlockedActions;
    isModal?: boolean;
    displayOptionsTop?: boolean;
}>();

const emit = defineEmits<{
    'update:chart-date': [IDateObject];
}>();

const uiStore = useUIStore();

const chartConfig = ref<IModuleChartConfig | null>(null);
const blockedActions = computed<IModuleBlockedActions>(() => props.blockedActions ?? NON_USER_DASHBOARD_BLOCKED_ACTIONS);

function updateChartDate (date: IDateObject): void {
    // Keep info.date in sync before dateChange runs (dashboard pattern).
    // Date-only changes must not rebuild chartConfig via setupChart — that races the fetch.
    syncChartDateFromProps();
    emit('update:chart-date', date);
}

function updateChartView (view: ViewMode): void {
    if (!chartConfig.value) return;
    const newModuleChartConfig = cloneDeep(chartConfig.value);
    newModuleChartConfig.info.extraChartInfo.currentDisplayOption = view;
    chartConfig.value = newModuleChartConfig;
}

function syncChartDateFromProps (): void {
    if (chartConfig.value?.info) {
        chartConfig.value.info.date = dateFormatToAnyFormat({ from: props.fromDate, to: props.toDate }, 'IDateRangeByStringWithType');
    }
}

async function setupChart (): Promise<void> {
    if (props.multipleSeries && props.multipleSeriesArray) {
        await chartApi.getJointSeriesConfig([{
            id: props.chartConfigId,
            name: '',
            chartSeries: props.multipleSeriesArray,
            date: '',
            extraChartInfo: {
                currentDisplayOption: ViewMode.Chart,
            },
        }]).then(res => {
            if (!res) return;
            chartConfig.value = {
                data: res[props.chartConfigId] as ITableDataConfig,
                info: {
                    date: dateFormatToAnyFormat({ from: props.fromDate, to: props.toDate }, 'IDateRangeByStringWithType'),
                    id: 'singleChart',
                    name: 'multipleChart',
                    size: 'Medium',
                    extraChartInfo: {},
                },
            };
        });
    } else if (!props.optionId) {
        const config = await chartApi.getJointSeriesConfigFromSingleId(props.seriesId, props.countryId);
        chartConfig.value = {
            data: config,
            info: {
                date: dateFormatToAnyFormat({ from: props.fromDate, to: props.toDate }, 'IDateRangeByStringWithType'),
                id: 'singleChart',
                name: 'singleChart',
                size: 'Medium',
                extraChartInfo: {},
            },
        };
    } else {
        const config = await chartApi.getJointSeriesConfig([{
            id: 'singleChart',
            name: '',
            size: '',
            chartSeries: [{
                dates: [],
                series: {
                    seriesId: props.seriesId, optionsId: props.optionId || null, country: props.countryId || null, isPlant: false,
                },
                isForDarkMode: uiStore.theme === 'dark',
            }],
            date: '',
            extraChartInfo: {},
        }]);
        chartConfig.value = {
            data: config.singleChart as ITableDataConfig,
            info: {
                date: dateFormatToAnyFormat({ from: props.fromDate, to: props.toDate }, 'IDateRangeByStringWithType'),
                id: 'singleChart',
                name: 'singleChart',
                size: 'Medium',
                extraChartInfo: {},
            },
        };
    }
}

// Date-only updates are applied in updateChartDate; rebuilding here races dateChange.
watch(() => [props.seriesId, props.optionId, props.multipleSeriesArray, props.countryId], () => {
    setupChart();
});

watch(() => [props.fromDate, props.toDate], () => {
    syncChartDateFromProps();
    setupChart();
});

setupChart();

</script>
